/**
 * SPDX-FileCopyrightText: 2023 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

import type { MaybeRef } from 'vue'
import type { Conversation, EventTimeRange } from '../types/index.ts'

import { toValue } from 'vue'
import { CONVERSATION, PARTICIPANT } from '../constants.ts'
import { getTalkConfig, hasTalkFeature } from '../services/CapabilitiesManager.ts'
import { ONE_HOUR_IN_MS } from './formattedTime.ts'

type Filter = 'unread' | 'mentions' | 'events'

const supportsArchive = hasTalkFeature('local', 'archived-conversations-v2')
const supportsAvatar = hasTalkFeature('local', 'avatar')

/**
 * Check if the conversation is classified (CLASSIFIED attribute bit is set)
 * Feature is supported with `classified-conversations` capability.
 *
 * @param conversation conversation object
 */
export function isClassifiedConversation(conversation: Conversation | undefined): boolean {
	return Boolean(conversation && conversation.attributes & CONVERSATION.ATTRIBUTE.CLASSIFIED)
}

/**
 * Check if the conversation is a channel (CHANNEL attribute bit is set)
 * Feature is supported with `announcement-preset` capability.
 *
 * @param conversation conversation object
 */
export function isChannelConversation(conversation: Conversation | undefined): boolean {
	return Boolean(conversation && conversation.attributes & CONVERSATION.ATTRIBUTE.CHANNEL)
}

/**
 * Check if the conversation is an announcement (CHANNEL+ANNOUNCEMENT attribute bits are set)
 * Feature is supported with `announcement-preset` capability.
 *
 * @param conversation conversation object
 */
export function isAnnouncementConversation(conversation: Conversation | undefined): boolean {
	return isChannelConversation(conversation) && Boolean(conversation!.attributes & CONVERSATION.ATTRIBUTE.ANNOUNCEMENT)
}

/**
 * check if the conversation has unread messages
 *
 * @param conversation conversation object
 */
export function hasUnreadMessages(conversation: Conversation): boolean {
	return conversation.unreadMessages > 0
}

/**
 * check if the conversation has unread mentions
 *
 * @param conversation conversation object
 */
export function hasUnreadMentions(conversation: Conversation): boolean {
	return conversation.unreadMention
		|| conversation.unreadMentionDirect
		|| (conversation.unreadMessages > 0
			&& (conversation.type === CONVERSATION.TYPE.ONE_TO_ONE || conversation.type === CONVERSATION.TYPE.ONE_TO_ONE_FORMER))
}

/**
 * check if the conversation has ongoing call
 *
 * @param conversation conversation object
 */
export function hasCall(conversation: Conversation): boolean {
	return conversation.hasCall && conversation.notificationCalls === PARTICIPANT.NOTIFY_CALLS.ON
}

/**
 * check whether the conversation is created to use an external call service
 *
 * @param conversation conversation object
 */
export function hasExternalCallService(conversation?: Conversation): boolean {
	return conversation?.objectType === CONVERSATION.OBJECT_TYPE.EXTERNAL_CALL
		&& !getTalkConfig('local', 'call', 'enabled')
		&& !!(getTalkConfig('local', 'call', 'external-call-service'))
}

/**
 * check if the conversation is an event conversation
 *
 * @param conversation conversation object
 */
export function isEvent(conversation: Conversation): boolean {
	return conversation.objectType === CONVERSATION.OBJECT_TYPE.EVENT
}

/**
 * Returns whether the conversation is a phone room (with a single SIP phone participant).
 * Covers both dial-out and direct-dial-in rooms.
 *
 * @param conversation - conversation object
 * @param conversation.objectId - conversation objectId
 * @param conversation.objectType - conversation objectType
 */
export function isConversationPhoneRoom({ objectId, objectType }: Conversation) {
	return [
		CONVERSATION.OBJECT_ID.PHONE_OUTGOING,
		CONVERSATION.OBJECT_ID.PHONE_INCOMING,
	].includes(objectId) && [
		CONVERSATION.OBJECT_TYPE.PHONE_LEGACY,
		CONVERSATION.OBJECT_TYPE.PHONE_PERSISTENT,
		CONVERSATION.OBJECT_TYPE.PHONE_TEMPORARY,
	].includes(objectType)
}

/**
 * check if the conversation is archived
 *
 * @param conversation conversation object
 * @param showArchived whether current filtered list is of archived conversation
 */
export function shouldIncludeArchived(conversation: Conversation, showArchived: boolean): boolean {
	return !supportsArchive || (conversation.isArchived === showArchived)
}

/**
 * Returns the start and end time of the event conversation
 *
 * @param conversation conversation object
 * @return start and end time in milliseconds
 */
export function getEventTimeRange(conversation: Conversation): EventTimeRange {
	if (!isEvent(conversation) || !conversation.objectId) {
		return { start: null, end: null }
	}
	const parts = conversation.objectId.split('#')
	if (parts.length !== 2) {
		return { start: null, end: null }
	}

	const [start, end] = parts.map((time) => Number(time) * 1000)
	if (isNaN(start) || isNaN(end)) {
		return { start: null, end: null }
	}

	return { start, end }
}

/**
 * check if the conversation is not an event conversation or if it is, check if it is happening in 16 hours
 *
 * @param conversation conversation object
 */
export function shouldIncludeEvents(conversation: Conversation): boolean {
	return !isEvent(conversation)
		|| (conversation.objectId?.includes('#') && shouldEventBeVisible(conversation))
}

/**
 * check if the conversation is happening in 16 hours
 *
 * @param conversation conversation object
 */
export function shouldEventBeVisible(conversation: Conversation): boolean {
	const startTime = getEventTimeRange(conversation).start
	if (!startTime) {
		return false
	}
	return startTime - Date.now() < 16 * ONE_HOUR_IN_MS
}

/**
 * apply the active filter
 *
 * @param conversation conversation object
 * @param filters the filter option
 */
export function filterConversation(conversation: Conversation, filters: Filter[]): boolean {
	if (filters.length === 0) {
		return shouldIncludeEvents(conversation)
	}
	return (!filters.includes('unread') || hasUnreadMessages(conversation))
		&& (!filters.includes('mentions') || hasUnreadMentions(conversation))
		&& (!filters.includes('events') || isEvent(conversation))
}

/**
 * check if the conversation is archived
 *
 * @param conversation conversation object
 * @param forceFallback whether fallback should be forced
 */
export function getFallbackIconClass(conversation: Conversation, forceFallback: boolean): string | undefined {
	if (conversation.isDummyConversation) {
		// Prevent a 404 when trying to load an avatar before the conversation data is actually loaded
		if (conversation.attributes & CONVERSATION.ATTRIBUTE.VOICE_ROOM) {
			return 'icon-voice-room'
		}
		return conversation.type === CONVERSATION.TYPE.PUBLIC ? 'icon-public' : 'icon-contacts'
	}

	if (!supportsAvatar || forceFallback) {
		if (conversation.objectType === CONVERSATION.OBJECT_TYPE.FILE
			|| conversation.type === CONVERSATION.TYPE.NOTE_TO_SELF) {
			return 'icon-file'
		} else if (conversation.objectType === CONVERSATION.OBJECT_TYPE.VIDEO_VERIFICATION) {
			return 'icon-password'
		} else if (conversation.objectType === CONVERSATION.OBJECT_TYPE.EMAIL) {
			return 'icon-mail'
		} else if (conversation.objectType === CONVERSATION.OBJECT_TYPE.PHONE_LEGACY
			|| conversation.objectType === CONVERSATION.OBJECT_TYPE.PHONE_PERSISTENT
			|| conversation.objectType === CONVERSATION.OBJECT_TYPE.PHONE_TEMPORARY) {
			return 'icon-phone'
		} else if (conversation.objectType === CONVERSATION.OBJECT_TYPE.EVENT) {
			return 'icon-event'
		} else if (conversation.objectType === CONVERSATION.OBJECT_TYPE.CIRCLES) {
			return 'icon-team'
		} else if (conversation.attributes & CONVERSATION.ATTRIBUTE.VOICE_ROOM) {
			return 'icon-voice-room'
		} else if (conversation.type === CONVERSATION.TYPE.CHANGELOG) {
			return 'icon-changelog'
		} else if (conversation.type === CONVERSATION.TYPE.ONE_TO_ONE_FORMER) {
			return 'icon-user'
		} else if (conversation.type === CONVERSATION.TYPE.GROUP) {
			return 'icon-contacts'
		} else if (conversation.type === CONVERSATION.TYPE.PUBLIC) {
			return 'icon-public'
		}
		return undefined
	}

	if (conversation.token) {
		// Existing conversations use the /avatar endpoint… Always!
		return undefined
	}

	if (conversation.objectType === CONVERSATION.OBJECT_TYPE.CIRCLES) {
		// Team icon for group conversation suggestions
		return 'icon-team'
	}

	if (conversation.type === CONVERSATION.TYPE.GROUP) {
		// Group icon for group conversation suggestions
		return 'icon-contacts'
	}

	// Fall-through for other conversation suggestions to user-avatar handling
	return undefined
}

/**
 * Sort list of conversation according to given group/order arguments (e.g. based on user preferences)
 *
 * @param list - Conversations list
 * @param groupMode - User selected grouping mode for conversations
 * @param sortOrder - User selected sort order for conversations
 */
export function sortConversationsList(
	list: MaybeRef<Conversation[]>,
	groupMode: typeof CONVERSATION.GROUP_MODE[keyof typeof CONVERSATION.GROUP_MODE],
	sortOrder: typeof CONVERSATION.SORT_ORDER[keyof typeof CONVERSATION.SORT_ORDER],
) {
	return toValue(list).slice()
		.sort((conversation1, conversation2) => {
			// Favorites always first
			if (conversation1.isFavorite !== conversation2.isFavorite) {
				return conversation1.isFavorite ? -1 : 1
			}

			if (groupMode !== CONVERSATION.GROUP_MODE.NONE) {
				const isOneToOne1 = [CONVERSATION.TYPE.ONE_TO_ONE, CONVERSATION.TYPE.ONE_TO_ONE_FORMER].includes(conversation1.type)
				const isOneToOne2 = [CONVERSATION.TYPE.ONE_TO_ONE, CONVERSATION.TYPE.ONE_TO_ONE_FORMER].includes(conversation2.type)

				if (isOneToOne1 !== isOneToOne2) {
					if (groupMode === CONVERSATION.GROUP_MODE.GROUP_FIRST) {
						// Group mode: groups first
						return isOneToOne1 ? 1 : -1
					} else if (groupMode === CONVERSATION.GROUP_MODE.PRIVATE_FIRST) {
						// Group mode: private first
						return isOneToOne1 ? -1 : 1
					}
				}
			}

			// Sort order: by alphabet A->Z
			if (sortOrder === CONVERSATION.SORT_ORDER.ALPHABETICAL) {
				return (conversation1.displayName).localeCompare(conversation2.displayName)
			}

			// Default (legacy) sort order: by recent activity
			return conversation2.lastActivity - conversation1.lastActivity
		})
}
